import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, combineLatest, map, of, shareReplay } from 'rxjs';
import {
  AddItemListRequest,
  BatchWriteItemOrRelationListRequest,
  BatchWriteItemOrRelationListResponse,
  CoreItemGate,
  GetItemListAndRelationListRequest,
  GetItemListAndRelationListResponse,
  GetItemListByCreatedByIdRequest,
  GetItemListByIdListRequest,
  GetItemListByTypeListRequest,
  GetKeysOnlyByCoreItemTypeListRequest,
} from '../../../../../backend/src/core/core-item/core-item.gate';
import { WorkspaceIdRequest } from '../../../../../backend/src/core/request-response';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { environment } from '../../../environments/environment';
import { AsyncServiceBase } from '../../shared/core/async-service-base';
import { LambdaService } from '../../shared/core/lambda.service';
import { LuxonUtils } from '../../shared/utils/luxon-utils';
import { SetUtils } from '../../shared/utils/set-utils';
import { RelationService } from '../relation/relation.service';
import { UserClerkService } from '../user/user-clerk.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { CoreItem, CoreItemBackend, CoreItemType, VertexRelation, VertexRelationAdd } from './core-item';
import { SortKeyUtils } from './sort-key';
import { SubDataConverter } from './sub-data.converter';

@Injectable({
  providedIn: 'root',
})
export class CoreItemService extends AsyncServiceBase<CoreItemBackend, CoreItem> {
  override convertFromFrontendToBackend(frontend: Partial<CoreItem>): CoreItemBackend {
    const backendBasic = {
      id: frontend.id!,
      sk: frontend.sk!,
      version: frontend.version!,
      ws: frontend.ws!,

      title: frontend.title,
      description: frontend.description!,

      parentId: frontend.parentId,
      type: frontend.type,

      subData: SubDataConverter.fromFrontendToBackend(frontend.coreType!, frontend.subData),
      unsafeSubData: frontend.unsafeSubData,
      publicSubData: frontend.publicSubData,

      status: frontend.status,
      statusTimeStr: frontend.statusTime?.toUTC().toISO() ?? undefined,

      scheduledTimeStr: frontend.scheduledTime?.toUTC().toISO() ?? undefined,

      startTimeStr: frontend.startTime?.toUTC().toISO() ?? undefined,
      endTimeStr: frontend.endTime?.toUTC().toISO() ?? undefined,

      userBoardConfig: frontend.userBoardConfig,
    };

    return backendBasic;
  }

  override afterAddItem(frontend: CoreItem): void {}
  override afterDeleteItem(frontend: CoreItem): void {}

  override convertFromBackendToFrontend(backend: CoreItemBackend): CoreItem {
    const coreType = SortKeyUtils.parse(backend.sk).coreItemType!;

    return {
      ...backend,
      subData: SubDataConverter.fromFrontendToBackend(coreType, backend.subData),
      coreType: coreType,

      createdBy: backend.createdById ? this._idAndItem.get(backend.createdById) : undefined,
      createdTime: DateTime.fromISO(backend.createdTimeStr!),

      lastModifiedBy: backend.lastModifiedById ? this._idAndItem.get(backend.lastModifiedById) : undefined,
      lastModifiedTime: DateTime.fromISO(backend.lastModifiedTimeStr!),

      statusTime: backend.statusTimeStr ? DateTime.fromISO(backend.statusTimeStr) : undefined,

      scheduledTime: backend.scheduledTimeStr ? DateTime.fromISO(backend.scheduledTimeStr) : undefined,

      startTime: backend.startTimeStr ? DateTime.fromISO(backend.startTimeStr) : undefined,
      endTime: backend.endTimeStr ? DateTime.fromISO(backend.endTimeStr) : undefined,
    };
  }
  override cleanTempData(payload?: any): void {}
  override afterUpdateItem(frontend: CoreItem): void {}

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(LambdaService) lambdaService: LambdaService,
    @Inject(UserClerkService) userClerkService: UserClerkService,
    private router: Router,
    private message: NzMessageService,
    private workspaceHolderService: WorkspaceHolderService,
    private relationService: RelationService
  ) {
    super(http, lambdaService, userClerkService, ResourceName.coreItem);
  }

  sendWeeklyReport$() {
    const request: WorkspaceIdRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
    };
    return this._lambdaService.run$(this._resourceName, CoreItemGate.actions.sendWeeklyReport, request);
  }

  updateCoreItem$(frontend: CoreItem, notChangingLoadingStatus = false) {
    return this.updateItemPrivate$(frontend, CoreItemGate.actions.update, true, notChangingLoadingStatus);
  }

  refreshCoreItemListFromServer$(coreTypeList: CoreItemType[]) {
    const request: GetItemListByTypeListRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      config: {
        coreTypeList,
      },
    };
    return this.updateItemOrItemListFromBackend$(CoreItemGate.actions.getItemListByTypeList, request);
  }

  getKeysOnlyByCoreItemTypeListNoCache$(
    coreItemTypeList: CoreItemType[],
    dateRange?: { startTime: DateTime; endTime: DateTime },
    createdById?: string
  ) {
    const request: GetKeysOnlyByCoreItemTypeListRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      coreItemTypeList,
      createdById,
    };

    if (dateRange) {
      request.dateRange = {
        startTimeStr: dateRange.startTime.toUTC().toISO()!,
        endTimeStr: dateRange.endTime.toUTC().toISO()!,
      };
    }

    return this._lambdaService.run$(this._resourceName, CoreItemGate.actions.getKeysOnlyByCoreItemTypeList, request);
  }

  getCoreItemListAndRelationList$(request: GetItemListAndRelationListRequest) {
    return combineLatest([
      this.itemListChanged.asObservable(),
      this._lambdaService.run$(this._resourceName, CoreItemGate.actions.getItemListAndRelationList, request).pipe(
        map((response: GetItemListAndRelationListResponse) => {
          const newItemList = response.queryList.itemList
            ?.map((item) => this.setOneItemToCacheData(item))
            .filter((c) => !!c);
          const newSecondLevelItemList = response.secondLevelQueryList?.itemList
            ?.map((item) => this.setOneItemToCacheData(item))
            .filter((c) => !!c);

          this.relationService.addRelationListInCache(response.queryList?.relationList);
          this.relationService.addRelationListInCache(response.secondLevelQueryList?.relationList);

          return {
            queryList: { itemList: newItemList, relationList: response.queryList?.relationList },
            secondLevelQueryList: {
              itemList: newSecondLevelItemList,
              relationList: response.secondLevelQueryList?.relationList,
            },
          };
        }),
        shareReplay(1)
      ),
    ]).pipe(map(([_, response]) => response));
  }

  getCoreItemListByIdList(idList: string[]): (CoreItem | undefined)[] {
    return idList.map((id) => this._idAndItem.get(id)).filter((c) => !!c);
  }

  getCoreItemListByIdList$(idList: string[]): Observable<CoreItem[]> {
    const idSet = new Set(idList);

    const request: GetItemListByIdListRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      idList: SetUtils.toArray(idSet),
    };

    const itemList: CoreItem[] = [];
    let hasAllItemList = true;

    idSet.forEach((id) => {
      const item = this._idAndItem.get(id);
      if (item) {
        itemList.push(item);
      } else {
        hasAllItemList = false;
      }
    });

    if (!hasAllItemList) {
      return combineLatest([
        this.itemListChanged.asObservable(),
        this.getItemOrItemListByAction$(CoreItemGate.actions.getItemListByIdList, request).pipe(shareReplay(1)),
      ]).pipe(
        map(([_, itemList]) => itemList.filter((item: any) => item?.id && idSet.has(item.id)).filter((c: any) => !!c))
      );
    } else {
      return of(itemList.filter((c: any) => !!c));
    }
  }

  getCoreItemListByBoard$(
    boardId: string,
    config?: { coreTypeList?: CoreItemType[]; dateRange?: { startTime?: DateTime; endTime?: DateTime } }
  ): Observable<CoreItem[]> {
    const request: GetItemListByCreatedByIdRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      createdById: boardId,
      config: {
        coreTypeList: config?.coreTypeList,
        startTimeStr: LuxonUtils.toIsoDay(config?.dateRange?.startTime?.toUTC()),
        endTimeStr: LuxonUtils.toIsoDay(config?.dateRange?.endTime?.toUTC()),
      },
    };

    const typeSet = SetUtils.fromArray(config?.coreTypeList);

    return combineLatest([
      this.itemListChanged.asObservable(),
      this.getItemOrItemListByAction$(CoreItemGate.actions.getItemListByCreatedById, request).pipe(shareReplay(1)),
    ]).pipe(
      map(([_, itemList]) =>
        itemList.filter((item: CoreItem) => {
          if (config?.coreTypeList && !typeSet.has(item.coreType)) {
            return false;
          }

          if (config?.dateRange?.startTime && item.createdTime && item.createdTime < config.dateRange?.startTime) {
            return false;
          }

          if (config?.dateRange?.endTime && item.createdTime && item.createdTime > config.dateRange?.endTime) {
            return false;
          }

          return item.createdById === boardId;
        })
      )
    );
  }

  getCoreItemList$(config: {
    coreTypeList?: CoreItemType[];
    parentIdList?: string[];
    dateRange?: {
      startTime: DateTime;
      endTime: DateTime;
    };
  }): Observable<CoreItem[]> {
    let request: GetItemListByTypeListRequest;

    if (config.dateRange) {
      request = {
        workspaceId: this.workspaceHolderService.currentWorkspace().id,
        config: {
          coreTypeList: config.coreTypeList,
          parentIdList: config.parentIdList,
          startTimeStr: LuxonUtils.toIsoDay(config.dateRange?.startTime?.toUTC()) ?? undefined,
          endTimeStr: LuxonUtils.toIsoDay(config.dateRange?.endTime?.toUTC()) ?? undefined,
        },
      };
    } else {
      request = {
        workspaceId: this.workspaceHolderService.currentWorkspace().id,
        config: {
          coreTypeList: config.coreTypeList,
          parentIdList: config.parentIdList,
        },
      };
    }

    const typeSet = SetUtils.fromArray(config.coreTypeList);
    const parentIdSet = SetUtils.fromArray(config.parentIdList);

    return combineLatest([
      this.itemListChanged.asObservable(),
      this.getItemOrItemListByAction$(CoreItemGate.actions.getItemListByTypeList, request).pipe(shareReplay(1)),
    ]).pipe(
      map(([_, itemList]) =>
        itemList.filter((item: CoreItem) => {
          if (config.coreTypeList && !typeSet.has(item.coreType)) {
            return false;
          }

          if (config.parentIdList && !parentIdSet.has(item.parentId)) {
            return false;
          }

          if (config.dateRange?.startTime && item.createdTime && item.createdTime < config.dateRange?.startTime) {
            return false;
          }

          if (config.dateRange?.endTime && item.createdTime && item.createdTime > config.dateRange?.endTime) {
            return false;
          }

          return true;
        })
      )
    );
  }

  get cachedItemList() {
    return this._itemOrItemList ? (this._itemOrItemList as CoreItem[]) : [];
  }

  updateCoreItemInCache(backend?: CoreItemBackend) {
    return this.setOneItemToCacheData(backend, true);
  }

  addItemList$(
    addItemList: {
      item: Partial<CoreItem>;
      coreType: CoreItemType;
      addRelationList?: VertexRelationAdd[];
    }[],
    deleteRelationList?: VertexRelation[],
    deleteMetadataList?: { id: string; sk: string }[],
    updateMetadataList?: CoreItem[]
  ) {
    if (environment.isConnectToBackend) {
      const workspaceId = this.workspaceHolderService.currentWorkspace().id;
      const request: AddItemListRequest = {
        workspaceId,
        addItemList: addItemList.map((c) => ({
          itemBackend: this.convertFromFrontendToBackend(c.item),
          coreType: c.coreType,
          addRelationList: c.addRelationList,
        })),
        deleteRelationList,
        deleteMetadataList,
        updateMetadataList: updateMetadataList?.map((c) => this.convertFromFrontendToBackend(c)),
      };

      return this._lambdaService.run$(ResourceName.coreItem, CoreItemGate.actions.addItemList, request).pipe(
        map((response: BatchWriteItemOrRelationListResponse) => {
          const addedCoreItemList: CoreItem[] = [];

          response.addedMetadataList?.forEach((c) => {
            const addedCoreItem = this.addBackendToService(c);
            addedCoreItemList.push(addedCoreItem!);
          });

          response.updatedMetadataList?.forEach((c) => {
            this.updateCoreItemInCache(c);
          });

          if (response.addedRelationList) {
            this.relationService.addRelationListInCache(response.addedRelationList);
          }

          if (deleteRelationList) {
            this.relationService.deleteRelationListInCache(deleteRelationList);
          }

          deleteMetadataList?.forEach((c) => {
            this.deleteBackendToServicePrivate(c as any);
          });

          return addedCoreItemList;
        })
      );
    }

    return of([]);
  }

  batchWriteMetadataAndRelationList$(options: {
    addRelationList?: VertexRelationAdd[];
    deleteRelationList?: VertexRelation[];
    addMetadataList?: Partial<CoreItem>[];
    deleteMetadataList?: { id: string; sk: string }[];
    updateMetadataList?: CoreItem[];
    notChangeLoadingStatus?: boolean;
  }): Observable<CoreItem[]> {
    let observable: Observable<CoreItem[]>;

    if (environment.isConnectToBackend) {
      const totalChangeCount =
        (options.addRelationList?.length ?? 0) +
        (options.deleteRelationList?.length ?? 0) +
        (options.addMetadataList?.length ?? 0) +
        (options.deleteMetadataList?.length ?? 0);

      if (totalChangeCount === 0) {
        return of([]);
      }

      let addMetadataList: any[] = [];
      if (options.addMetadataList) {
        addMetadataList = options.addMetadataList.map((c) => {
          return {
            type: c.coreType,
            metadata: this.convertFromFrontendToBackend(c),
          };
        });
      }

      const updateMetadataBackendList = options.updateMetadataList?.map((c) => this.convertFromFrontendToBackend(c));

      const batchWriteRelationListRequest: BatchWriteItemOrRelationListRequest = {
        workspaceId: this.workspaceHolderService.currentWorkspace().id,
        addRelationList: options.addRelationList,
        deleteRelationList: options.deleteRelationList,
        addMetadataList,
        deleteMetadataList: options.deleteMetadataList,
        updateMetadataList: updateMetadataBackendList,
      };
      observable = this._lambdaService
        .run$(ResourceName.coreItem, CoreItemGate.actions.batchWriteItemOrRelationList, batchWriteRelationListRequest, {
          notChangeLoadingStatus: options.notChangeLoadingStatus,
        })
        .pipe(
          map((response: BatchWriteItemOrRelationListResponse) => {
            const addedCoreItemList: CoreItem[] = [];

            response.addedMetadataList?.forEach((c) => {
              const addedCoreItem = this.addBackendToService(c);
              addedCoreItemList.push(addedCoreItem!);
            });

            response.updatedMetadataList?.forEach((c) => {
              this.updateCoreItemInCache(c);
            });

            if (options.addRelationList) {
              this.relationService.addRelationListInCache(response.addedRelationList);
            }

            if (options.deleteRelationList) {
              this.relationService.deleteRelationListInCache(options.deleteRelationList);
            }

            // response.addedRelationList?.forEach((c) => {
            //   this.setTaskRelationToCache(c);
            // });

            // options.deleteRelationList?.forEach((c) => {
            //   this.deleteRelationFromCache(c);
            // });

            options.deleteMetadataList?.forEach((c) => {
              this.deleteBackendToServicePrivate(c as any);
            });

            return addedCoreItemList;
          })
        );
    } else {
      observable = of([]);
    }
    return observable;
  }
}
